import { z } from 'zod';
import { UsdAmount } from './ai.js';
/**
 * AI viết chi tiết một mục của `.chilldoc` — APM-SWR-a, docs/plan-section-writer.md §3.3.
 *
 * Web gửi phần thân của **một** mục (markdown, node cấu trúc thay bằng chỗ giữ `⟦obj:N⟧`) cùng
 * ngữ cảnh; skill `write_section` trả markdown mới cho phần thân ấy; web áp nó thành **đề xuất**
 * (chèn / xoá theo dõi được) để người dùng duyệt. Các hàm thuần cuối tệp dùng chung hai phía:
 * server kiểm output trong `finalize`, web và test đo cùng một thước.
 */
// ---------------------------------------------------------------------------
// Hằng số
// ---------------------------------------------------------------------------
/** `more` — Chi tiết hơn (≈2×) · `much` — Rất chi tiết (≈4×). */
export const SECTION_WRITE_DETAILS = ['more', 'much'];
export const SectionWriteDetail = z.enum(SECTION_WRITE_DETAILS);
/**
 * Trần phần thân gửi đi (ký tự markdown). Lượt chạy đi qua endpoint chung nên không đặt được trần
 * output riêng — trần mặc định 16 000 token; `much` ≈ 4 × 6 000 ký tự ≈ 9–10k token tiếng Việt
 * vẫn chừa chỗ cho phần suy nghĩ (plan §3.4).
 */
export const SECTION_WRITE_MAX_SOURCE = 6_000;
/** Trần mỗi phần ngữ cảnh (dàn ý, mục lân cận). */
export const SECTION_WRITE_MAX_CONTEXT = 8_000;
export const SECTION_WRITE_MAX_INSTRUCTION = 500;
/** Trần output — chặn một bản viết lạc đề dài vô tận trước khi web phải parse nó. */
export const SECTION_WRITE_MAX_DRAFT = 60_000;
/** Tỉ lệ độ dài nhắm tới (nói với model) và tối thiểu (finalize kiểm), cùng sàn cho mục ngắn / rỗng. */
export const SECTION_WRITE_LENGTH = {
    more: { target: 2, targetFloor: 600, min: 1.6, minFloor: 400 },
    much: { target: 4, targetFloor: 1400, min: 3, minFloor: 1000 },
};
// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------
export const SECTION_KINDS = ['heading', 'listItem', 'selection'];
export const SectionKind = z.enum(SECTION_KINDS);
export const SectionWriteRequest = z.object({
    kind: SectionKind,
    /** Chữ tiêu đề / đoạn đầu của mục danh sách — giữ nguyên, không thuộc phần được viết. Rỗng khi `selection`. */
    title: z.string().trim().max(500),
    /** Số mục đang hiển thị (`6.1`) khi đọc được — để model hiểu vị trí. */
    numbering: z.string().trim().max(40).nullable(),
    /** Cấp tiêu đề của mục (`heading`); null với loại khác. */
    headingLevel: z.number().int().min(1).max(6).nullable(),
    body: z.string().max(SECTION_WRITE_MAX_SOURCE),
    /** Node cấu trúc trong phần thân, theo thứ tự chỗ giữ `⟦obj:N⟧`. */
    objects: z.array(z.object({ id: z.string().max(40), kind: z.string().max(40) })).max(50),
    outline: z.string().max(SECTION_WRITE_MAX_CONTEXT),
    neighbours: z.string().max(SECTION_WRITE_MAX_CONTEXT),
    detail: SectionWriteDetail,
    instruction: z.string().trim().max(SECTION_WRITE_MAX_INSTRUCTION),
    language: z.enum(['vi', 'en', 'auto']),
});
export const SectionDraft = z.object({
    /** Markdown thay cho `body` gốc. */
    body: z.string().trim().min(1).max(SECTION_WRITE_MAX_DRAFT),
    /** Một câu cho người dùng (vd. "nên kiểm lại …"), hoặc null. */
    note: z.string().trim().max(300).nullable(),
});
/** Body của `POST /me/files/section-write` — request cộng mã dấu vết của lượt chạy. */
export const SectionWriteInput = SectionWriteRequest.extend({
    runId: z.uuid().optional(),
});
export const SectionWriteResult = z.object({
    draft: SectionDraft,
    /** Chuỗi thập phân như mọi số tiền AI khác. */
    costUsd: UsdAmount,
    /** Hết lượt sửa mà bản viết vẫn chưa đủ dài — `draft.note` nói rõ. */
    partial: z.boolean(),
    /** Mã lượt chạy — web dùng làm `authorId` (`ai:<runId>`) của đề xuất. */
    runId: z.string(),
});
// ---------------------------------------------------------------------------
// Chỗ giữ node cấu trúc
// ---------------------------------------------------------------------------
export const objectPlaceholder = (index) => `⟦obj:${index}⟧`;
const PLACEHOLDER = /⟦obj:(\d+)⟧/g;
const HAS_PLACEHOLDER = /⟦obj:\d+⟧/;
/** Số thứ tự các chỗ giữ, theo thứ tự xuất hiện. */
export function placeholdersOf(text) {
    return [...text.matchAll(PLACEHOLDER)].map((match) => Number(match[1]));
}
// ---------------------------------------------------------------------------
// Chữ thuần và độ dài
// ---------------------------------------------------------------------------
const WORD = /[\p{L}\p{M}\p{N}]+/gu;
/** Markdown → chữ đọc được: bỏ dấu khối (`#`, `-`, `1.`, `>`), dấu nhấn, chỗ giữ. */
export function plainText(markdown) {
    return markdown
        .replace(PLACEHOLDER, ' ')
        .split('\n')
        .map((line) => line
        .replace(/^\s*(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s?)/, '')
        .replace(/[*_`~]+/g, '')
        .trim())
        .filter(Boolean)
        .join('\n');
}
export const plainLength = (markdown) => plainText(markdown).length;
/** Độ dài (ký tự chữ thuần) nhắm tới và tối thiểu cho một phần thân gốc. */
export function lengthGoal(body, detail) {
    const rule = SECTION_WRITE_LENGTH[detail];
    const source = plainLength(body);
    return {
        source,
        target: Math.max(Math.round(source * rule.target), rule.targetFloor),
        min: Math.max(Math.round(source * rule.min), rule.minFloor),
    };
}
// ---------------------------------------------------------------------------
// Ý gốc
// ---------------------------------------------------------------------------
/** Một ý của phần thân: mục danh sách, tiêu đề con hoặc đoạn — chữ thuần của một dòng khối. */
export function sectionIdeas(markdown) {
    const ideas = [];
    let paragraph = [];
    const flush = () => {
        const text = plainText(paragraph.join(' '));
        if (text)
            ideas.push(text);
        paragraph = [];
    };
    for (const line of markdown.split('\n')) {
        if (!line.trim() || HAS_PLACEHOLDER.test(line)) {
            flush();
            continue;
        }
        if (/^\s*(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>)/.test(line)) {
            flush();
            const text = plainText(line);
            if (text)
                ideas.push(text);
        }
        else
            paragraph.push(line);
    }
    flush();
    return ideas;
}
function wordsOf(text) {
    return (text.toLowerCase().match(WORD) ?? []).filter((word) => word.length > 1 || /\d/.test(word));
}
/**
 * Phần từ của `idea` có mặt trong `block` (0…1). **Độ phủ**, không phải Dice: ý gốc ngắn đứng
 * trong một khối đã viết dài gấp ba vẫn phải được nhận là còn.
 */
export function wordCoverage(idea, block) {
    const need = new Set(wordsOf(idea));
    if (need.size === 0)
        return 1;
    const have = new Set(wordsOf(block));
    let found = 0;
    for (const word of need)
        if (have.has(word))
            found++;
    return found / need.size;
}
/** Ý ngắn (≤ 3 từ, thường là nhãn như "Ưu điểm") phải còn đủ từ; ý dài được viết lại một nửa. */
const coverageNeeded = (idea) => (wordsOf(idea).length <= 3 ? 1 : 0.5);
/**
 * Các ý gốc **không** còn trong bản viết, theo thứ tự gốc. Ý được coi là còn khi một khối của bản
 * viết, đứng **sau** khối khớp ý trước đó, phủ đủ từ của nó — giữ cả thứ tự.
 */
export function lostIdeas(source, draft) {
    const blocks = sectionIdeas(draft);
    const lost = [];
    let cursor = 0;
    for (const idea of sectionIdeas(source)) {
        const need = coverageNeeded(idea);
        let found = -1;
        for (let i = cursor; i < blocks.length; i++) {
            if (wordCoverage(idea, blocks[i]) >= need) {
                found = i;
                break;
            }
        }
        if (found < 0)
            lost.push(idea);
        else
            cursor = found + 1;
    }
    return lost;
}
// ---------------------------------------------------------------------------
// Trích dẫn và cú pháp ngoài dòng chảy
// ---------------------------------------------------------------------------
/** `[3]`, `[1, 2]`, `[4–6]` (không phải link `[3](…)`, không phải ô checklist). */
const CITATION = /\[(\d+(?:\s*[,–-]\s*\d+)*)\](?!\()/g;
export function citationsOf(text) {
    const out = new Set();
    for (const match of text.matchAll(CITATION)) {
        for (const part of match[1].split(',')) {
            const [from, to] = part.split(/[–-]/).map((value) => Number(value.trim()));
            if (from === undefined || !Number.isFinite(from))
                continue;
            const end = to !== undefined && Number.isFinite(to) && to >= from && to - from <= 50 ? to : from;
            for (let n = from; n <= end; n++)
                out.add(n);
        }
    }
    return out;
}
const NON_FLOW = [
    ['table', /^\s*\|.*\|\s*$/m],
    ['code', /^\s*(```|~~~)/m],
    ['math', /\$\$|(^|[^\\\w$])\$(?!\s)[^$\n]+?(?<!\s)\$(?![\w$])/],
    ['image', /!\[[^\]]*\]\(/],
    ['html', /<\/?[a-zA-Z][\w-]*(\s[^<>]*)?\/?>/],
];
/**
 * Cú pháp mà chế độ đề xuất không theo dõi được (bảng, khối mã, công thức, ảnh, HTML) có trong
 * `markdown` — rỗng là chỉ có đoạn, danh sách, tiêu đề, trích dẫn và dấu nhấn trong dòng.
 */
export function nonFlowSyntax(markdown) {
    // Inline code may legitimately hold `$`, `|` or `<tag>`; fences are checked before it is blanked.
    const withoutCode = markdown.replace(/`[^`\n]+`/g, '``');
    return NON_FLOW.filter(([kind, pattern]) => pattern.test(kind === 'code' ? markdown : withoutCode)).map(([kind]) => kind);
}
//# sourceMappingURL=section-write.js.map